import { Field, FieldFooter, Select } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { usePositionFormFieldPositionsQuery } from 'components/domain/position/PositionFormField/usePositionFormFieldPositionsQuery';
import { useReduxSelector } from 'hooks';
import { isNil, last } from 'lodash';
import { DateTime } from 'luxon';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { ClassName } from 'types/ClassName';

interface IPositionFormFieldProps {
  className?: ClassName;
  form: any;
  name: string;
  onChange?: (value: string | '') => void;
  validations?: any;
  autofocus?: boolean;
  showAllPositions?: boolean;
  placeholder?: string;
}

export const PositionFormField: FunctionComponent<IPositionFormFieldProps> = (props) => {
  const { form, name, validations, className, onChange, showAllPositions, placeholder, ...other } = props;

  const authMember = useReduxSelector((state) => state.authMember);
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
  const positionsQuery = usePositionFormFieldPositionsQuery({
    showAllPositions,
    positionLevel: authMember.position?.level,
  });
  const formValue = useMemo(() => form.state.data[name], [form.state.data, name]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (refreshKey) {
      positionsQuery.refetch();
    }
  }, [refreshKey]);

  useEffect(() => {
    if (positionsQuery.data && positionsQuery.data.length > 0) {
      const newOptions = positionsQuery.data.map(({ id, title }) => ({ value: id, label: title }));

      setOptions(newOptions);

      // an empty string for placeholder is valid and should prevent auto selection
      if (isNil(placeholder) && isNil(formValue)) {
        const initSelection = last(positionsQuery.data);
        if (initSelection) {
          form.handleChange(name, initSelection.id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionsQuery.data, name, formValue, placeholder]);

  const renderField = useCallback(
    ({ ...fieldProps }) => {
      function handleOnChange(val: string | '') {
        fieldProps.handleChange(val, onChange);
      }

      return (
        <React.Fragment key={formValue}>
          <Select
            error={fieldProps.hasError}
            options={options}
            value={formValue}
            onChange={handleOnChange}
            placeholder={placeholder}
            onClick={() => setRefreshKey(DateTime.local().toSeconds())} // refresh options when dropdown is clicked
            {...other}
          />
          <FieldFooter errorMessage={fieldProps.errorMessage} hasError={fieldProps.hasError} />
        </React.Fragment>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formValue, options, placeholder, other]
  );

  const classes = classNames({ 'project-picker-form-field': true }, className);

  return <Field form={form} name={name} validations={validations} className={classes} render={renderField} />;
};

export default PositionFormField;
